<template>
  <ConfirmDialog ref="confirm" />
  <ConfirmDialog ref="description">
    <template v-slot:content>
      <v-card-text
          class="py-0 px-0 mt-3 text-black content-label"
      >
        {{ $t('Укажите описание товара, работы или услуги') }}
      </v-card-text>
      <TextArea
          v-model.trim="tempDescription"
          placeholder="Введите описание"
          :rows="3"
          maxRows="3"
          maxlength="255"
          class="description-grid-field"
          :disabled="!canEdit"
      />
    </template>
    <template v-slot:cancelText>{{ $t('Отменить') }}</template>
    <template v-slot:agreeText>{{ $t('Сохранить') }}</template>
  </ConfirmDialog>
  <LoadedPositionsDialog
    ref="loadedPosDialog"
    v-bind:positions="positions"
  />
  <v-form ref="form">
  <Card>
    <div class="cardTitle">
      {{ $t('Спецификация') }}{{positionsQuantityPattern}}
      <span v-if="positionsInfoStore.isPositionsRequired" class="text-error">*</span>
    </div>
    <DataTableVirtual
      v-if="canShowPositionsTable"
      disable-sort
      fixed-header
      hide-default-footer
      style="max-height: 580px; overflow-y: auto;"
      class="no-table-border propos"
      :headers="headers"
      :items="positions"
      :items-per-page="-1"
    >
<!--    Для каждого поля добавлены пропсы persistent-hint и hint="' '" для создания и отображения блоков
        генерируемых самим vuetify    -->
      <template v-slot:item.name="{ item }">
        <TextArea
          v-model.trim="item.name"
          @change="$emit('changed')"
          :rules="[].concat(validation.required(true))"
          placeholder="Наименование"
          :rows="1"
          maxRows="3"
          maxlength="255"
          class="position-grid-fields"
          :disabled="!canEdit"
        />
      </template>
      <template v-slot:item.measureId="{ item }">
        <AutoCompleteInput
            class="position-grid-fields"
            placeholder="Ед. измерения"
            no-data-text="Нет совпадений"
            v-model="item.measureId"
            @change="$emit('changed')"
            :items="measures"
            :rules="[].concat(validation.required(true))"
            :disabled="!canEdit"
        ></AutoCompleteInput>
      </template>
      <template v-slot:item.quantity="{ item }">
        <CountInput
          placeholder="Кол-во"
          :precision="{min: 0, max: 2}"
          v-model="item.quantity"
          @change="$emit('changed')"
          :rules="[].concat(validation.number(0.01, 999_999.99, true, true, true))"
          class="position-grid-fields"
          :disabled="!canEdit"
        />
      </template>
      <template v-slot:item.startMaxPrice="{ item }" v-if="positionsInfoStore.showStartMaxPrice">
        <MoneyInput
          class="position-grid-fields money-input"
          placeholder="НМЦ за позицию"
          v-model="item.startMaxPrice"
          @change="$emit('changed')"
          :rules="[].concat(validation.number(0.01, 999_999_999_999.99, true, true, true))"
          :disabled="!canEdit"
          hide-details="auto"
        />
      </template>
      <template v-slot:item.actions="{ item }">
        <v-menu transition="slide-x-transition" location="bottom right">
          <template v-slot:activator="{ props }">
            <v-btn
              variant="text"
              v-bind="props"
              icon="mdi-dots-vertical"
              :disabled="!canEdit"
            />
          </template>

          <v-list class="py-0">
            <v-list-item @click="editDescription(item)" class="px-4" :disabled="!canEdit">
              <v-list-item-title>
                {{ $t((!!item.description && String(item.description).trim().length > 0 ? 'Редактировать' : 'Добавить') + ' описание') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="removePosition(item)" class="px-4" :disabled="!canEdit">
              <v-list-item-title class="text-error">{{ $t('Удалить') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </DataTableVirtual>
    <span style="color: rgba(var(--error-color))" v-if="!canAddNewPosition">{{ $t('Достигнуто максимальное количество позиций') }}</span>
    <div class="d-flex">
      <v-btn
        prepend-icon="mdi-plus"
        color="primary"
        density="compact"
        variant="elevated"
        class="ml-4 rounded"
        size="small"
        @click="addNewPositions"
        :disabled="!canAddNewPosition || !canEdit"
      >{{ $t('Добавить') }}</v-btn>
      <v-btn
          v-if="canShowPositionsTable"
          color="primary"
          density="compact"
          variant="outlined"
          class="ml-4 rounded"
          size="small"
          @click="removeAllPositions"
          :disabled="!canEdit"
      >{{ $t('Удалить все') }}</v-btn>
      <v-spacer/>
      <v-dialog>
        <template v-slot:activator="{ props: activatorProps }">
          <v-btn
            color="primary"
            density="compact"
            variant="outlined"
            class="ml-4 rounded"
            size="small"
            v-bind="activatorProps"
            :disabled="!canEdit"
          >{{ $t('Загрузить из Excel') }}</v-btn>
        </template>
        <template v-slot:default="{ isActive }">
          <FileInput
            style="width: 40%; align-self: center"
            title="Добавление из Excel"
            is-edit
            class="mb-3"
            :documents="documents"
            :patterns="patterns"
            :upload-file="(file) => onUpload(file) && (isActive.value = false)"
            :downloadPattern="excelPositionsPattern"
            :uploadExtensions="availableDownloadTypes"
          >
            <template v-slot:file-icon="{ document }">
              <FileIcon :document="document" />
            </template>
          </FileInput>
        </template>
      </v-dialog>
    </div>
    <div
      v-if="!positionsInfoStore.hidePartOfVolume"
      class="partOfVolume"
      v-show="canShowPositionsTable"
    >
      <div class="cardTitle">{{ $t('Параметры подачи предложений') }}</div>
      <div class="cardSubTitle">{{ $t(subtitleByPartOfVolume) }}</div>
      <v-checkbox
        v-model="partOfVolumeValue"
        :label="$t('Подача заявок на часть объема')"
        base-color="black"
        hide-details
        :disabled="!canEdit"
      />
    </div>
  </Card>
  </v-form>
</template>

<script>
import Card from "@intec/vue-components/form/Card.vue";
import TextArea from "@intec/vue-components/form/TextArea.vue";
import CountInput from "@/components/form/CountInput.vue";
import validation from "@/components/validation";
import AutoCompleteInput from "@intec/vue-components/form/AutoCompleteInput.vue";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import FileInput from "@intec/vue-components/form/FileInput.vue";
import LoadedPositionsDialog from "@/components/positions/LoadedPositionsDialog.vue";
import { toast } from '@/lib/toast';
import api from "@/api";
import { useLibraryStore } from "@/stores/library";
import { onMounted, watch } from "vue";
import { usePositionsInfoStore } from "@/stores/positionsInfoStore";
import FileIcon from "@/components/FileIcon.vue";
import DataTableVirtual from "@intec/vue-components/table/DataTableVirtual.vue"
import MoneyInput from "@/components/form/MoneyInput.vue";

export default {
  name: "PositionsInfo",
  components: {
    MoneyInput,
    FileIcon,
    ConfirmDialog,
    LoadedPositionsDialog,
    AutoCompleteInput,
    Card,
    TextArea,
    CountInput,
    FileInput,
    DataTableVirtual,
  },
  props: {
    procedureType: {
      type: String
    },
    measures: {
      type: Array,
      required: true,
    },
    positions: {
      type: Array
    },
    partOfVolume: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: true,
    }
  },
  emits: ['update:positions', 'update:partOfVolume', 'changed'],
  setup(props, { emit }) {
    const positionsInfoStore = usePositionsInfoStore();
    const libraryStore = useLibraryStore();

    onMounted(() => {
      libraryStore.callActionByCode('initPositionsInfoOptions', {
        procedureType: props.procedureType
      });
    });

    watch(
      () => props.procedureType,
      (newType) => {
        positionsInfoStore.setDefaults();
        libraryStore.callActionByCode('initPositionsInfoOptions', {
          procedureType: newType
        });
        if (positionsInfoStore.hidePartOfVolume) {
          emit('update:partOfVolume', false);
        }
      }
    );

    // Если цена не отображается, значение startMaxPrice должно быть null
    watch(
      () => positionsInfoStore.showStartMaxPrice,
      (newVal) => {
        if (!newVal) {
          props.positions.forEach(pos => {
            pos.startMaxPrice = null;
          });
          emit('changed');
        }
      }
    );

    return {
      positionsInfoStore,
      libraryStore
    };
  },
  data() {
    return {
      documents: [],
      patterns: [{
        displayName: 'Скачать шаблон для заполнения',
        fileName: 'Шаблон_позиции_ТРУ.xlsx',
        href: '/procedures/loadExcelPattern'
      }],
      validation,
      height: '500px', // Высота для прокрутки
      availableDownloadTypes: [
        'application/vnd.ms-excel',
        'application/excel',
        'application/x-excel',
        'application/x-msexcel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      maxFileMBSize: 20,
      currentItem: null,
      tempDescription: null
    }
  },
  methods: {
    async editDescription(item) {
      this.currentItem = item;
      this.tempDescription = this.currentItem.description;

      const confirmed = await this.$refs.description.open(
          'Описание позиции',
      );

      if (confirmed) {
        this.currentItem.description = this.tempDescription;
        this.$emit('changed');
      }

      this.currentItem = null;
      this.tempDescription = null;
    },
    addNewPositions() {
      this.positions.push({
        name: null,
        measureId: null,
        quantity: null,
        description: null,
        startMaxPrice: null,
      });
      this.$emit('changed');
    },
    async removeAllPositions() {
      let approve = await this.$refs.confirm.open(
          'Удаление позиций ТРУ',
          'Вы действительно хотите удалить все позиции?',
      );
      if (approve) {
        this.positions.splice(0);
        this.$emit('changed');
      }
    },
    removePosition(item) {
      this.positions.splice(this.positions.findIndex(pos => pos === item),1);
      this.$emit('changed');
    },
    async validate() {
      return this.$refs.form.validate();
    },
    async resetValidation() {
      this.$refs.form.resetValidation();
    },
    async onUpload(file) {
      const data = new FormData();
      data.append('file', file);
      try {
        if (file.size > this.maxFileBytesSize)
          throw new Error(this.$t(`Ошибка: Файл: {_1}. Превышает максимальный размер {_2} Мбайт.`, {'_1': file.name, '_2': this.maxFileMBSize}));

        // Пытаемся получить ссылку от внешнего модуля.
        const overrideLink = this.libraryStore.callFunctionByCode('getLinkExcelPositions', {
          type: this.procedureType
        })?.linkExcelPositions ?? null;

        // Если модуль вернул строку — используем её, иначе дефолтную.
        const uploadUrl = typeof overrideLink === 'string'
          ? overrideLink
          : '/procedures/loadExcelPositions';

        let resp = await api.post(uploadUrl, data);
        await this.$refs.loadedPosDialog.performCheck(resp.positions);
      } catch (error) {
        toast.error(error.message);
      }
    },
    excelPositionsPattern(pattern) {
      // Пытаемся получить ссылку от внешнего модуля.
      const overrideLink = this.libraryStore.callFunctionByCode('getLinkExcelPattern', {
        type: this.procedureType
      })?.linkExcelPattern ?? null;

      // Если модуль вернул строку — используем её, иначе дефолтную.
      window.location.href = typeof overrideLink === 'string'
        ? overrideLink
        : pattern.href;
    }
  },
  computed: {
    headers() {
      const headers = [
        {
          title: 'Наименование',
          key: 'name',
          headerProps: { 'data-required': 'true' },
          width: this.positionsInfoStore.showStartMaxPrice ? '35%' : '55%'
        },
        {
          title: 'Единица измерения',
          key: 'measureId',
          headerProps: { 'data-required': 'true' },
          width: '20%'
        },
        {
          title: 'Кол-во',
          key: 'quantity',
          headerProps: { 'data-required': 'true' },
          width: '20%'
        }
      ];

      if (this.positionsInfoStore.showStartMaxPrice) {
        headers.push({
          title: 'НМЦ за позицию',
          key: 'startMaxPrice',
          headerProps: { 'data-required': 'true' },
          width: '20%'
        });
      }

      headers.push({
        key: 'actions',
        width: '5%',
        align: 'end'
      });

      return headers;
    },
    partOfVolumeValue: {
      get() {
        return this.partOfVolume;
      },
      set(value) {
        this.$emit('update:partOfVolume', value)
      }
    },
    subtitleByPartOfVolume() {
      return this.partOfVolume ? 'Участник сможет предложить свое количество и стоимость по каждой позиции.'
        : 'Участник сможет указать стоимость для количества, указанного организатором по всем позициям.';
    },
    maxFileBytesSize() {
      return this.maxFileMBSize * 1024 * 1024;
    },
    canAddNewPosition() {
      return this.positions.length < 500;
    },
    canShowPositionsTable() {
      return this.positions.length > 0;
    },
    positionsQuantityPattern() {
      return this.positions.length > 0 ? `(${this.positions.length})` : '';
    }
  }
}
</script>

<style scoped>
:deep(.v-data-table) {
  th {
    background-color: #F6F6F6 !important;
    .v-data-table-header__content {
      font-size: 16px;
      color: #000000 !important;
    }
  }
  [data-required] {
    span::after {
      content: ' *';
      color: rgba(var(--error-color));
    }
  }
  .v-table__wrapper > table > tbody > tr > td {
    padding: 0;
  }
  .v-table__wrapper > table > tbody > tr > td:not(:last-child) {
    align-content: baseline;
  }
}

.no-table-border {
  border: none;
  border-radius: 0;
}

.position-grid-fields {
  margin: 10px 5px;
}

.description-grid-field {
  margin: 18px 0;
}

.content-label,
:deep(.v-label) {
  color: rgb(var(--black-color))!important;
  font-family: Abrade;
  font-size: 16px!important;
  font-style: normal;
  font-weight: 500;
  line-height: normal!important;
}

.partOfVolume > * {
  margin-top: 5px;
  margin-bottom: 5px;
}

:deep(.v-checkbox) {
  .v-selection-control {
    min-height: fit-content !important;
  }
  .v-selection-control--density-default {
    --v-selection-control-size: 30px !important;
  }
  .v-label {
    opacity: 1;
    font-weight: 600;
  }
}

/* Кастомные стили, чтобы Input не отличался от TextArea в Спецификации. */
.money-input :deep(.v-field) {
  height: 56px !important;
}

.money-input :deep(.v-field__input) {
  font-size: 16px;
}

.money-input :deep(.v-field--disabled) {
  background-color: transparent !important;
  opacity: var(--v-disabled-opacity) !important;
}

.money-input :deep(.v-messages__message) {
  font-size: 12px;
  line-height: 12px;
  word-break: break-word;
  color: rgb(var(--v-theme-error));
  margin-bottom: 0;
}

.money-input :deep(.v-field:hover),
.money-input :deep(.v-field:focus-within) {
  border-color: rgb(var(--blue-blue-secondary-hover)) !important;
}

.money-input :deep(.v-field.v-field--error:hover),
.money-input :deep(.v-field.v-field--error:focus-within) {
  border-color: rgb(var(--error-color)) !important;
}
</style>
